import { AxiosError } from 'axios';

import useUpdateMultiChannelOrderStatus from '../../../../hooks/queries/multiChannel/useUpdateMultiChannelOrderStatus';
import toastMsg from '../../../../lib/toastMsg';
import { ORDER_STATUS } from '../../../../constants/service/app/multiChannelConstants';
import { asyncSimpleModal } from '../../../../lib/common';
import { OrderManagementItem } from '../../../../types/multiChannel/orderManagement';

type UpdateOrderStatusParam = {
  ids: string[];
  status: 'cancel' | 'return' | 'productReady';
  onSuccess?: () => void;
  onError?: () => void;
};

type UpdateOrderStatus = {
  cancelOrders: (param: Omit<UpdateOrderStatusParam, 'status'>) => void;
  returnOrders: (param: Omit<UpdateOrderStatusParam, 'status'>) => void;
  readyOrders: (param: Omit<UpdateOrderStatusParam, 'status'>) => void;
};

type UpdateOrderStatusProps = {
  orderList: OrderManagementItem[];
};

const useUpdateOrderStatus = (props: UpdateOrderStatusProps): UpdateOrderStatus => {
  const { orderList } = props;
  const { mutate: updateOrderStatusMutate } = useUpdateMultiChannelOrderStatus();

  const updateOrderStatus = async ({ ids, status, onSuccess, onError }: UpdateOrderStatusParam): Promise<void> => {
    if (ids.length === 0) {
      toastMsg.error('선택 된 주문이 없습니다.');
      return;
    }

    const selectedOrderList = orderList.filter((order) => ids.includes(order.id));
    const validOrderIdList: string[] = [];
    selectedOrderList.forEach((order) => {
      if (
        status === 'cancel' &&
        (order.status === ORDER_STATUS.ACCEPT || order.status === ORDER_STATUS.PRODUCT_READY)
      ) {
        validOrderIdList.push(order.id);
      } else if (
        status === 'return' &&
        (order.status === ORDER_STATUS.DELIVERY_DONE || order.status === ORDER_STATUS.DELIVERY_SHIPPING)
      ) {
        validOrderIdList.push(order.id);
      } else if (status === 'productReady' && order.status === ORDER_STATUS.ACCEPT) {
        validOrderIdList.push(order.id);
      }
    });

    const title = status === 'cancel' ? '주문 취소' : status === 'return' ? '반품 처리' : '상품 준비';
    const confirm = await asyncSimpleModal({
      type: 'confirm',
      title: title,
      message: `변경 가능한 주문은 ${validOrderIdList.length}건입니다. 변경하시겠습니까?`,
      okText: '확인',
      cancelText: '취소',
    });

    if (!confirm) return;

    updateOrderStatusMutate(
      { ids: validOrderIdList, status: status },
      {
        onSuccess,
        onError: async (error: unknown) => {
          if (error instanceof AxiosError && error.response?.data.status !== 400) return;
          const confirm = await asyncSimpleModal({
            type: 'error',
            title: '처리 불가',
            message: '변경 불가한 주문이 있습니다.\n다시 시도해주세요.',
          });

          if (!confirm) return;

          if (onError) onError();
        },
      },
    );
  };

  const cancelOrders = ({ ids, onSuccess, onError }: Omit<UpdateOrderStatusParam, 'status'>): void => {
    updateOrderStatus({
      ids,
      status: ORDER_STATUS.CANCEL,
      onSuccess,
      onError,
    });
  };

  const returnOrders = ({ ids, onSuccess, onError }: Omit<UpdateOrderStatusParam, 'status'>): void => {
    updateOrderStatus({
      ids,
      status: ORDER_STATUS.RETURN,
      onSuccess,
      onError,
    });
  };

  const readyOrders = ({ ids, onSuccess, onError }: Omit<UpdateOrderStatusParam, 'status'>): void => {
    updateOrderStatus({
      ids,
      status: ORDER_STATUS.PRODUCT_READY,
      onSuccess,
      onError,
    });
  };

  return {
    cancelOrders,
    returnOrders,
    readyOrders,
  };
};

export default useUpdateOrderStatus;
